import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const COOKIE_KEY = 'cpuc_cookie_consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [animOut, setAnimOut] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      // Petit délai pour ne pas bloquer le rendu initial
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const dismiss = (choice) => {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({
      choice,                              // 'accepted' | 'declined'
      date: new Date().toISOString(),
    }));
    setAnimOut(true);
    setTimeout(() => setVisible(false), 350);
  };

  if (!visible) return null;

  return (
    <>
      {/* Overlay très léger pour attirer l'attention */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 1999,
          opacity: animOut ? 0 : 1,
          transition: 'opacity 0.35s ease',
          pointerEvents: 'none',
        }}
      />

      {/* Bannière */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Consentement aux cookies"
        style={{
          position: 'fixed',
          bottom: '1.5rem',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(680px, calc(100vw - 2rem))',
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18), 0 2px 8px rgba(0,0,0,0.08)',
          padding: '1.5rem',
          zIndex: 2000,
          opacity: animOut ? 0 : 1,
          transform: animOut
            ? 'translateX(-50%) translateY(20px)'
            : 'translateX(-50%) translateY(0)',
          transition: 'opacity 0.35s ease, transform 0.35s ease',
          border: '0.5px solid rgba(0,0,0,0.08)',
        }}
      >
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>

          {/* Icône */}
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: '#fce8e8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <i className="bi bi-shield-lock-fill" style={{ color: 'var(--cpuc-red)', fontSize: '18px' }}></i>
          </div>

          {/* Texte */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--cpuc-black)' }}>
              Nous respectons votre vie privée
            </p>
            <p style={{ margin: 0, fontSize: '0.82rem', color: '#666', lineHeight: 1.6 }}>
              Ce site utilise des cookies pour améliorer votre expérience. Conformément à la{' '}
              <strong>Loi 25</strong> du Québec, nous vous demandons votre consentement avant
              tout dépôt de cookies non essentiels.{' '}
              <Link
                to="/politique-confidentialite"
                style={{ color: 'var(--cpuc-red)', fontWeight: 600, textDecoration: 'none' }}
              >
                En savoir plus
              </Link>
            </p>
          </div>
        </div>

        {/* Boutons */}
        <div style={{
          display: 'flex',
          gap: '0.75rem',
          marginTop: '1.25rem',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={() => dismiss('declined')}
            style={{
              padding: '0.6rem 1.25rem',
              borderRadius: '999px',
              border: '1.5px solid #ddd',
              background: 'transparent',
              color: '#555',
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
              transition: 'border-color 0.2s, color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#999'; e.currentTarget.style.color = '#333'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#ddd'; e.currentTarget.style.color = '#555'; }}
          >
            Refuser
          </button>

          <button
            onClick={() => dismiss('accepted')}
            style={{
              padding: '0.6rem 1.5rem',
              borderRadius: '999px',
              border: 'none',
              background: 'var(--cpuc-red)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.85rem',
              cursor: 'pointer',
              boxShadow: '0 4px 14px rgba(229,53,43,0.35)',
              transition: 'box-shadow 0.2s, transform 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(229,53,43,0.5)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(229,53,43,0.35)'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <i className="bi bi-check2 me-1"></i>
            Accepter
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * Hook utilitaire — à utiliser dans tes autres composants
 * pour savoir si l'utilisateur a accepté les cookies.
 *
 * Usage :
 *   const consent = useCookieConsent();
 *   if (consent === 'accepted') { // charge Google Analytics, etc. }
 */
export function useCookieConsent() {
  const raw = localStorage.getItem(COOKIE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw).choice; // 'accepted' | 'declined'
  } catch {
    return null;
  }
}